// Environment validation: Only allow production domain (app.surveilx.net)
const isAllowedEnvironment = () => {
  const hostname = window.location.hostname;
  // Debug mode: Allow testing on any environment if ?posthogDebug=true
  const debugMode = new URLSearchParams(window.location.search).get('posthogDebug') === 'true';

  // Only allow production domain, block staging IP, localhost, and other domains
  const isProduction = hostname === 'app.surveilx.net';

  return isProduction || debugMode;
};

const logEvent = (eventName, properties = {}) => {
  console.log(`📤 PostHog Event: ${eventName}`, properties);
};

const captureEvent = (eventName, properties = {}) => {
  if (!isAllowedEnvironment()) {
    console.log(`⛔ PostHog event "${eventName}" blocked: Not in allowed environment`);
    return;
  }

  try {
    if (!window.posthog) {
      console.warn('⚠️ PostHog not initialized');
      return;
    }

    if (!window.posthog.capture) {
      console.warn('⚠️ PostHog capture function not available');
      return;
    }

    const eventData = {
      ...properties,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    window.posthog.capture(eventName, eventData);
    logEvent(eventName, eventData);

    if (window.posthog.flush) {
      setTimeout(() => {
        window.posthog.flush();
      }, 100);
    }

  } catch (error) {
    console.error(`❌ Failed to capture event "${eventName}":`, error);
  }
};

export const trackFormSubmission = (formType, data = {}) => {
  captureEvent('form_submitted', {
    form_type: formType,
    ...data,
  });
};

export const trackScanInitiated = (scanData = {}) => {
  captureEvent('scan_initiated', {
    domain: scanData.domain,
    scanType: scanData.scanType,
    language: scanData.language,
    ...scanData,
  });
};

export const trackPaymentInitiated = (paymentData = {}) => {
  captureEvent('payment_initiated', {
    amount: paymentData.amount,
    currency: paymentData.currency,
    planType: paymentData.planType,
    paymentIntentId: paymentData.paymentIntentId,
    ...paymentData,
  });

  if (!isAllowedEnvironment()) return;

  if (window.posthog) {
    window.posthog.register({
      payment_initiated: true,
      payment_amount: paymentData.amount,
      payment_currency: paymentData.currency,
      payment_intent_id: paymentData.paymentIntentId,
    });
  }
};

export const trackPaymentCompleted = (paymentData = {}) => {
  captureEvent('payment_completed', {
    amount: paymentData.amount,
    currency: paymentData.currency,
    planType: paymentData.planType,
    status: paymentData.status,
    paymentIntentId: paymentData.paymentIntentId,
    ...paymentData,
  });

  if (!isAllowedEnvironment()) return;

  if (window.posthog) {
    window.posthog.register({
      payment_completed: true,
      payment_status: paymentData.status,
      payment_amount: paymentData.amount,
      payment_currency: paymentData.currency,
      payment_intent_id: paymentData.paymentIntentId,
      payment_error: paymentData.error || null,
    });
  }
};

export const trackReportGenerated = (reportData = {}) => {
  captureEvent('report_generated', {
    domain: reportData.domain,
    riskScore: reportData.riskScore,
    ...reportData,
  });
};

export const trackEmailVerification = (email, status) => {
  captureEvent('email_verification', {
    email,
    status,
  });
};

export const trackError = (errorType, errorMessage, context = {}) => {
  captureEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    ...context,
  });
};

export const trackPageNavigation = (fromPage, toPage) => {
  captureEvent('page_navigation', {
    from_page: fromPage,
    to_page: toPage,
  });
};

export const trackUserIdentification = (userId, userProperties = {}) => {
  if (!isAllowedEnvironment()) {
    console.log('⛔ PostHog user identification blocked: Not in allowed environment');
    return;
  }

  try {
    if (!window.posthog) {
      console.warn('⚠️ PostHog not initialized');
      return;
    }

    if (!window.posthog.identify) {
      console.warn('⚠️ PostHog identify function not available');
      return;
    }

    window.posthog.identify(userId, userProperties);
    console.log(`📤 PostHog User Identified: ${userId}`, userProperties);

    if (window.posthog.flush) {
      setTimeout(() => {
        window.posthog.flush();
      }, 100);
    }

  } catch (error) {
    console.error('❌ Failed to identify user:', error);
  }
};

export const getPostHogStatus = () => {
  const isAllowed = isAllowedEnvironment();
  const status = {
    environmentAllowed: isAllowed,
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    debugMode: new URLSearchParams(window.location.search).get('posthogDebug') === 'true',
    initialized: !!window.posthog,
    hasCapture: !!window.posthog?.capture,
    hasIdentify: !!window.posthog?.identify,
    hasFlush: !!window.posthog?.flush,
    hasRegister: !!window.posthog?.register,
    sessionId: window.posthog?.get_session_id?.(),
    distinctId: window.posthog?.get_distinct_id?.(),
    timestamp: new Date().toISOString(),
  };
  console.log('📊 PostHog Status:', status);
  return status;
};
